/**
 * ChatMessageList - renders the full conversation as a scrollable list.
 *
 * Maps state.turns to either UserTurn bubbles or ChatRunView components.
 * Scrolls to the bottom when new turns arrive or text streams in.
 * Detects scroll-to-top to trigger loading older history pages.
 */

import type { ChatTriggerSource, RunOrigin } from '@protoss/shared';
import { Pill } from '@protoss/ui';
import * as React from 'react';
import type { ConversationTurn, HistoryStatus } from '../state/chatState';
import type { ChatRunViewProps } from './ChatRunView';
import { ChatRunView } from './ChatRunView';

interface ChatMessageListProps {
  turns: ConversationTurn[];
  historyStatus: HistoryStatus;
  hasMoreHistory: boolean;
  onLoadOlderHistory: () => void;
  onConfirm: ChatRunViewProps['onConfirm'];
  onDeny: ChatRunViewProps['onDeny'];
  onRetry: ChatRunViewProps['onRetry'];
  onAction?: ChatRunViewProps['onAction'];
  onActionSettled?: ChatRunViewProps['onActionSettled'];
  onRespondPrompt?: ChatRunViewProps['onRespondPrompt'];
}

/** Pill labels for button/system-initiated runs; typed messages render no pill. */
const TRIGGER_SOURCE_LABELS: Partial<Record<ChatTriggerSource, string>> = {
  button_enrich: 'Enrich company',
  button_transcript: 'Analyze document',
  button_generate: 'Generate estimate',
  button_action: 'Automated action',
  user_retry: 'Retry',
};

function UserBubble({
  prompt,
  triggerSource,
  origin,
}: {
  prompt: string;
  triggerSource?: ChatTriggerSource;
  origin?: RunOrigin;
}) {
  // Prefer the precise origin (exact page + button label). Fall back to the
  // coarse per-trigger-source label for legacy rows / buttons without an origin.
  const pillLabel = origin
    ? `${origin.page} · ${origin.label}`
    : triggerSource
      ? TRIGGER_SOURCE_LABELS[triggerSource]
      : undefined;
  return (
    <div className="ai-chat-user-bubble-group">
      <div className="ai-chat-user-bubble">
        <p className="ai-chat-user-bubble__text">{prompt}</p>
      </div>
      {pillLabel && (
        <div className="ai-chat-user-bubble-meta">
          <Pill variant="muted">{pillLabel}</Pill>
        </div>
      )}
    </div>
  );
}

// Survives unmount/remount (pane close → reopen). Only one ChatMessageList
// exists at a time so a module-level pair is sufficient.
let _savedScrollTop = -1;
let _savedPinnedToBottom = true;

export function ChatMessageList({
  turns,
  historyStatus,
  hasMoreHistory,
  onLoadOlderHistory,
  onConfirm,
  onDeny,
  onRetry,
  onAction,
  onActionSettled,
  onRespondPrompt,
}: ChatMessageListProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Initialise from saved state so the turns effect respects prior position.
  const pinnedToBottomRef = React.useRef(_savedPinnedToBottom);
  const suppressScrollRef = React.useRef(false);

  const prevScrollHeightRef = React.useRef(0);
  const isLoadingOlderRef = React.useRef(false);

  // Save on unmount; restore on remount (handles pane close → reopen).
  // Declared before the turns effect so it runs first on mount: if NOT pinned,
  // we restore scrollTop here and the turns effect (which checks pinnedToBottom)
  // will skip its scroll. If pinned, restore is a no-op and turns effect handles it.
  React.useLayoutEffect(() => {
    if (_savedScrollTop >= 0 && !pinnedToBottomRef.current && scrollRef.current) {
      suppressScrollRef.current = true;
      scrollRef.current.scrollTop = _savedScrollTop;
      requestAnimationFrame(() => {
        suppressScrollRef.current = false;
      });
    }
    return () => {
      _savedScrollTop = scrollRef.current?.scrollTop ?? -1;
      _savedPinnedToBottom = pinnedToBottomRef.current;
    };
  }, []);

  const scrollToBottom = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    suppressScrollRef.current = true;
    el.scrollTop = el.scrollHeight;
    // scroll events fire asynchronously; clear the flag after the next frame
    requestAnimationFrame(() => {
      suppressScrollRef.current = false;
    });
  }, []);

  // On turns change: scroll to bottom if pinned, or restore anchor after older-history load.
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (isLoadingOlderRef.current) {
      suppressScrollRef.current = true;
      el.scrollTop = el.scrollHeight - prevScrollHeightRef.current;
      isLoadingOlderRef.current = false;
      requestAnimationFrame(() => {
        suppressScrollRef.current = false;
      });
      return;
    }
    if (pinnedToBottomRef.current) {
      scrollToBottom();
    }
  }, [turns, scrollToBottom]);

  // Stable refs so handlers never need to be recreated.
  const onLoadOlderRef = React.useRef(onLoadOlderHistory);
  onLoadOlderRef.current = onLoadOlderHistory;
  const hasMoreRef = React.useRef(hasMoreHistory);
  hasMoreRef.current = hasMoreHistory;

  const handleScroll = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el || suppressScrollRef.current) return;

    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottomRef.current = distFromBottom < 50;

    if (!isLoadingOlderRef.current && hasMoreRef.current && el.scrollTop < 80) {
      prevScrollHeightRef.current = el.scrollHeight;
      isLoadingOlderRef.current = true;
      onLoadOlderRef.current();
    }
  }, []);

  const latestRunId = React.useMemo(() => {
    for (let i = turns.length - 1; i >= 0; i--) {
      const t = turns[i];
      if (t?.kind === 'assistant') return t.runId;
    }
    return null;
  }, [turns]);

  if (historyStatus === 'loading' && turns.length === 0) {
    return (
      <div
        className="ai-chat-messages ai-chat-messages--loading"
        aria-busy="true"
        aria-live="polite"
      >
        <span className="ai-chat-messages__loading-hint">Loading conversation&hellip;</span>
      </div>
    );
  }

  if (turns.length === 0) {
    return (
      <div className="ai-chat-messages ai-chat-messages--empty">
        <p className="ai-chat-messages__empty-hint">Ask anything...</p>
      </div>
    );
  }

  const isLoadingOlder = historyStatus === 'loading' && turns.length > 0;

  return (
    <div
      ref={scrollRef}
      className="ai-chat-messages"
      role="log"
      aria-live="polite"
      aria-relevant="additions"
      onScroll={handleScroll}
    >
      {isLoadingOlder ? (
        <div className="ai-chat-messages__older-loader" aria-live="polite" aria-busy="true">
          Loading earlier messages&hellip;
        </div>
      ) : hasMoreHistory ? (
        <div className="ai-chat-messages__load-earlier">
          <button
            className="ai-chat-messages__load-earlier-btn"
            type="button"
            onClick={() => {
              const el = scrollRef.current;
              if (el) prevScrollHeightRef.current = el.scrollHeight;
              isLoadingOlderRef.current = true;
              onLoadOlderHistory();
            }}
          >
            Load earlier messages
          </button>
        </div>
      ) : null}
      {turns.map((turn) => {
        if (turn.kind === 'user') {
          return (
            <UserBubble
              key={turn.id}
              prompt={turn.prompt}
              triggerSource={turn.triggerSource}
              origin={turn.origin}
            />
          );
        }
        return (
          <ChatRunView
            key={turn.runId}
            run={turn}
            onConfirm={onConfirm}
            onDeny={onDeny}
            onRetry={onRetry}
            onAction={onAction}
            onActionSettled={onActionSettled}
            isLatestRun={turn.runId === latestRunId}
            onRespondPrompt={onRespondPrompt}
          />
        );
      })}
    </div>
  );
}
